import styles from './Icon.module.css';
import { type IconName, iconRegistry } from './registry';

export interface IconProps {
    /** The concept to draw — never a glyph name, so the drawing can change without a sweep. */
    readonly name: IconName;
    /** `sm` = `icon/sm`, `md` = `icon/md` (§4). Defaults to `md`. */
    readonly size?: 'sm' | 'md';
    /** `bold` takes `icon/stroke-bold`; the default is `icon/stroke` 1.75 (§4). */
    readonly emphasis?: 'regular' | 'bold';
}

/**
 * A decorative glyph, sized and stroked from the `icon/*` tokens.
 *
 * `aria-hidden` is unconditional and there is no prop to turn it off. Every §5 row that carries an
 * icon also carries visible text beside it — StatusIndicator's mandatory label, the empty state's
 * one-line explanation, an icon-only button's `aria-label` — so an icon here is always a duplicate
 * of something a screen reader already reads. Leaving it in the accessibility tree makes it read
 * twice; a `label` prop would invite the icon to become the only label, which §2's build rule
 * ("no state is ever carried by colour alone", and by extension by shape alone) forbids.
 *
 * Geometry comes from CSS, not from lucide's `size`/`strokeWidth` props, and that is not a style
 * preference: those props land as SVG presentation attributes, which cannot hold a `var()`. Setting
 * width/height/stroke-width in the stylesheet is the only way this renders from tokens (FS-3 NFR-1) —
 * and CSS beats a presentation attribute, so lucide's own 24/2 defaults lose cleanly.
 */
export function Icon({ name, size = 'md', emphasis = 'regular' }: IconProps) {
    const Glyph = iconRegistry[name];
    return <Glyph aria-hidden="true" className={`${styles.icon} ${styles[size]} ${styles[emphasis]}`} />;
}
